import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { DataGrid } from "@mui/x-data-grid";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";

// ?jobId= names ONE posting (FI-02, tester report 2026-10-17: a job's "View applicants" listed every
// applicant). Digits only and above zero; anything else is no filter.
const parseJobId = (raw) => {
  if (typeof raw !== "string" || !/^[0-9]+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
};

const jobLabel = (job) =>
  `${job.title || "Untitled"} (#${job.id}${String(job.status) === "1" ? "" : ", inactive"})`;

const CareersApplicants = () => {
  const [loading, setLoading] = useState(false);
  // Paged on the SERVER (marketing staff, 2026-10): the API answers at most 200 applicants a page (they are
  // PII), so fetching "1000" once and paging in the browser silently lost everyone past the newest 200.
  // loadedKey / loadedJobId name the filter and page the rows and total were answered for (Codex P2, 2026-10-17).
  const [pageState, setPageState] = useState({
    rows: [], total: 0, page: 1, pageSize: 10, loadedKey: null, loadedJobId: undefined,
  });
  const token = localStorage.getItem("accessToken");
  const [searchParams, setSearchParams] = useSearchParams();
  const hasJobParam = searchParams.has("jobId");
  const jobId = parseJobId(searchParams.get("jobId"));
  // The postings for the Position filter. The job list needs Careers:View, which an applicants-only seat may
  // lack: a refusal leaves the filter as the URL names it and is not reported (the grid is what matters here).
  const [jobs, setJobs] = useState({ status: "loading", rows: [] });
  // Only the newest request may fill the grid: a late answer for an earlier filter or page is dropped.
  const requestSeq = useRef(0);
  const lastJobId = useRef(jobId);
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const viewKey = `${jobId === null ? "" : jobId}|${pageState.page}|${pageState.pageSize}`;

  const fetchApplicants = async (seq) => {
    const key = viewKey;
    const forJob = jobId;
    try {
      setLoading(true);
      setLoadError(false);
      const data = { page: pageState.page, limit: pageState.pageSize };
      if (jobId !== null) data.job_id = jobId;
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API}admin/get-job-applicants`,
        headers: { "x-access-token": token },
        data,
      });
      if (seq !== requestSeq.current) return;
      setPageState((old) => ({
        ...old,
        rows: response?.data?.data?.rows || [],
        total: Number(response?.data?.data?.count) || 0,
        loadedKey: key,
        loadedJobId: forJob,
      }));
      setLoading(false);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      setLoading(false);
      setLoadError(true);
      Swal.fire({
        title: error?.response?.data?.message || "Failed to load applicants",
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    }
  };

  useEffect(() => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    // A job reached another way (Back / Forward, an edited URL) starts at page 1, as one chosen here does.
    if (lastJobId.current !== jobId) {
      lastJobId.current = jobId;
      if (pageState.page !== 1) {
        setPageState((old) => ({ ...old, page: 1 }));
        return;
      }
    }
    fetchApplicants(seq);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.page, pageState.pageSize, jobId, reloadTick]);

  // Rows are shown only for the filter and page they were answered for: while a new filter or page loads,
  // and after it fails, nothing from the previous one stays on screen under the new label. The total is kept
  // across pages of the same filter (the pager stays put) and dropped when the filter changes.
  const rowsOnScreen = pageState.loadedKey === viewKey ? pageState.rows : [];
  const totalOnScreen = pageState.loadedJobId === jobId ? pageState.total : 0;

  useEffect(() => () => { requestSeq.current += 1; }, []);

  useEffect(() => {
    let cancelled = false;
    axios({
      method: "post",
      url: `${process.env.REACT_APP_API}admin/get-listing-jobopeing`,
      headers: { "x-access-token": token },
      data: {},
    })
      .then((response) => {
        const rows = response?.data?.data?.rows;
        if (!cancelled) setJobs({ status: "loaded", rows: Array.isArray(rows) ? rows : [] });
      })
      .catch(() => {
        if (!cancelled) setJobs({ status: "failed", rows: [] });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jobIsListed = jobId !== null && jobs.rows.some((j) => String(j.id) === String(jobId));

  // An invalid ?jobId= is dropped at once; an unknown one once the job list shows it does not exist. Both
  // leave all applicants on screen. A refused job list keeps a well-formed id: the API decides.
  useEffect(() => {
    if (!hasJobParam) return;
    if (jobId === null || (jobs.status === "loaded" && !jobIsListed)) {
      const next = new URLSearchParams(searchParams);
      next.delete("jobId");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasJobParam, jobId, jobs.status, jobIsListed]);

  const chooseJob = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("jobId", value);
    else next.delete("jobId");
    setPageState((old) => ({ ...old, page: 1 }));
    setSearchParams(next);
  };

  // Resumes are private now (no public URL). Fetch the PDF through the
  // admin-authenticated endpoint and open it in a new tab, instead of linking a
  // world-readable blob URL.
  const downloadResume = async (id) => {
    try {
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_API}admin/job-applicant-resume/${id}`,
        headers: { "x-access-token": token },
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      // Anchor-with-download rather than window.open(): a programmatic
      // window.open() after an await is gated by the popup blocker (and blocked
      // outright on Safari), which fails silently. A download anchor is not
      // popup-gated and always works.
      const a = document.createElement("a");
      a.href = url;
      a.download = `resume-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // responseType:'blob' means the server's JSON error body arrives as a Blob,
      // so read it back to surface the real message instead of a bare generic one.
      let message = "Could not load resume";
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          message = JSON.parse(text)?.message || message;
        } else if (error?.response?.data?.message) {
          message = error.response.data.message;
        }
      } catch (_) {
        /* keep the generic message */
      }
      Swal.fire({
        title: message,
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "candidate_name",
      headerName: "Candidate",
      flex: 1,
      minWidth: 160,
    },
    { field: "position", headerName: "Position", flex: 1, minWidth: 160 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
    { field: "phone", headerName: "Phone", flex: 1, minWidth: 140 },
    {
      field: "file_path",
      headerName: "Resume",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        params.value ? (
          <button
            type="button"
            onClick={() => downloadResume(params.row.id)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "#1976d2",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Download
          </button>
        ) : (
          "Not provided"
        ),
    },
    {
      field: "createdAt",
      headerName: "Applied",
      flex: 1,
      minWidth: 130,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "",
    },
  ];

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Applicants" />
      <div className="datatable">
        <div className="datatableTitle">
          <Link to="/careers" className="link">
            Back to Careers
          </Link>
          <div
            className="formInput"
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, fontSize: 16 }}
          >
            <label htmlFor="applicants-position">Position</label>
            <select
              id="applicants-position"
              value={jobId === null ? "" : String(jobId)}
              onChange={(e) => chooseJob(e.target.value)}
              disabled={jobs.status === "loading"}
            >
              <option value="">All positions</option>
              {jobs.rows.map((job) => (
                <option key={job.id} value={String(job.id)}>
                  {jobLabel(job)}
                </option>
              ))}
              {jobId !== null && !jobIsListed && (
                <option value={String(jobId)}>{`Job #${jobId}`}</option>
              )}
            </select>
            {jobId !== null && (
              <button
                type="button"
                className="link"
                style={{ background: "none" }}
                onClick={() => chooseJob("")}
              >
                Show all
              </button>
            )}
          </div>
        </div>
        {loadError && (
          <div role="alert" style={{ margin: "0 0 10px", fontSize: 14 }}>
            Could not load the applicants.{" "}
            <button
              type="button"
              onClick={() => setReloadTick((t) => t + 1)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "#1976d2",
                textDecoration: "underline",
                cursor: "pointer",
                font: "inherit",
              }}
            >
              Retry
            </button>
          </div>
        )}
        <DataGrid
          className="datagrid"
          autoHeight
          rows={rowsOnScreen}
          rowCount={totalOnScreen}
          // The grid holds ONE server page, so its own sorting and filtering would only reorder or search
          // those rows and silently miss everyone on other pages (Codex admin review). Off; newest first, as
          // the API orders them.
          columns={columns.map((c) => ({ ...c, sortable: false, filterable: false }))}
          disableColumnFilter
          disableColumnMenu
          page={pageState.page - 1}
          pageSize={pageState.pageSize}
          rowsPerPageOptions={[10, 30, 50, 100]}
          pagination
          paginationMode="server"
          onPageChange={(newPage) => setPageState((old) => ({ ...old, page: newPage + 1 }))}
          onPageSizeChange={(newPageSize) => setPageState((old) => ({ ...old, page: 1, pageSize: newPageSize }))}
          getRowId={(row) => row.id}
        />
      </div>
    </PageShell>
  );
};

export default CareersApplicants;
